import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { message, Modal } from "antd";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
} from "react-bootstrap";
import MyContext from "MyContext";

const { confirm } = Modal;

function CertificatePayment() {
  const { endPointUrl, Token } = useContext(MyContext);
  const [payments, setPayments] = useState([]);

  // Fetch payments
  const getAllPayments = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${endPointUrl}/get-all-certificate-payment`,
        { Token }
      );
      if (data.status) {
        setPayments(data.data || []);
      } else {
        message.warning(data.message || "Failed to load payments");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      message.error("Something went wrong while fetching payments");
    }
  }, [endPointUrl, Token]);

  useEffect(() => {
    getAllPayments();
  }, [getAllPayments]);

  // Table data & columns
  const data = useMemo(() => (Array.isArray(payments) ? payments : []), [payments]);

  const columns = useMemo(
    () => [
      {
        Header: "Certificate Payer",
        accessor: "user",
        Cell: ({ value }) =>
          `${value?.Name ?? ""} ${value?.Last_Name ?? ""}`.trim(),
      },
      {
        Header: "Certificate Test",
        accessor: "skillTest.SKT_Title",
        Cell: ({ value }) => value ?? "-",
      },
      {
        Header: "Amount",
        accessor: "Amount",
        Cell: ({ value }) => `$${value ?? 0}`,
      },
      {
        Header: "Payment Date",
        accessor: "Payment_date",
        Cell: ({ value }) =>
          value
            ? new Intl.DateTimeFormat("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
              }).format(new Date(value))
            : "-",
      },
      {
        Header: "Payment Reference",
        accessor: "PaymentReference",
        Cell: ({ value }) => value ?? "-",
      },
    ],
    []
  );

  // React-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    setGlobalFilter,
    state: { globalFilter, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <Card>
            <Card.Header>
              <Card.Title as="h4">All Certificate Payments</Card.Title>
            </Card.Header>
          </Card>

          <Card className="strpied-tabled-with-hover">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Form.Control
                type="search"
                placeholder="Search Payment ..."
                style={{ maxWidth: "900px" }}
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </Card.Header>

            <Card.Body className="table-full-width table-responsive px-0">
              <Table {...getTableProps()} className="table-hover table-striped">
                <thead>
                  {headerGroups.map((headerGroup) => {
                    const { key, ...rest } = headerGroup.getHeaderGroupProps();
                    return (
                      <tr key={key} {...rest}>
                        {headerGroup.headers.map((column) => {
                          const { key: colKey, ...colRest } = column.getHeaderProps(
                            column.getSortByToggleProps()
                          );
                          return (
                            <th key={colKey} {...colRest}>
                              {column.render("Header")}
                              <span>
                                {column.isSorted
                                  ? column.isSortedDesc
                                    ? " 🔽"
                                    : " 🔼"
                                  : ""}
                              </span>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {page.length > 0 ? (
                    page.map((row) => {
                      prepareRow(row);
                      const { key: rowKey, ...rowRest } = row.getRowProps();
                      return (
                        <tr key={rowKey} {...rowRest}>
                          {row.cells.map((cell) => {
                            const { key: cellKey, ...cellRest } =
                              cell.getCellProps();
                            return (
                              <td key={cellKey} {...cellRest}>
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-3">
                        No payments found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center px-3 py-2">
                <div>
                  Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
                </div>
                <div>
                  <Button
                    variant="outline-secondary"
                    onClick={previousPage}
                    disabled={!canPreviousPage}
                  >
                    Previous
                  </Button>{" "}
                  <Button
                    variant="outline-secondary"
                    onClick={nextPage}
                    disabled={!canNextPage}
                  >
                    Next
                  </Button>
                </div>
                <Form.Select
                  style={{ width: "120px" }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[5, 10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default CertificatePayment;
